import React from "react"
import { useSelector } from "react-redux"

import { RootState, useAppDispatch } from "@root/store"
import {
  getFeatureFlag,
  setLocaleCountryOverride,
  setLocaleLanguageOverride,
} from "@root/store/thunks"
import { selectLocaleOverride } from "@root/store/selectors"
import RemovableStringValueInput from "@root/components/RemovableStringValueInput"
import {
  Box,
  Button,
  Grid,
  Paper,
  TextField,
  Typography,
} from "@material-ui/core"
import { useSettingStyles } from "../styles"
import SnackContext from "@root/components/Snackbar"
import FeatureFlagCheckTable from "@root/components/FeatureFlagCheckTable"

const Metadata: React.FC = ({}) => {
  const classes = useSettingStyles()

  const dispatch = useAppDispatch()
  const {
    error,
    status,
    featureFlags = [],
  } = useSelector((state: RootState) => state.metadata)
  const localeOverride = useSelector(selectLocaleOverride)
  const overrideLanguage = React.useCallback(
    (value?: string) => {
      dispatch(setLocaleLanguageOverride(value))
    },
    [dispatch]
  )
  const overrideCountry = React.useCallback(
    (value?: string) => {
      dispatch(setLocaleCountryOverride(value))
    },
    [dispatch]
  )
  const loadFeatureFlag = React.useCallback(
    (flagName: string) => dispatch(getFeatureFlag(flagName)),
    [dispatch]
  )
  const { createSnack } = React.useContext(SnackContext)
  const [flagName, setFlagName] = React.useState("")

  React.useEffect(() => {
    if (error) {
      createSnack(error)
    }
  }, [error])

  return (
    <>
      <Paper
        className={classes.paper}
        data-qa-selector="metadata-locale-override"
      >
        <Typography variant="h4">Locale Override</Typography>
        <RemovableStringValueInput
          id="language"
          label="Language"
          minLength={2}
          maxLength={2}
          initialValue={localeOverride?.language}
          onValueSet={(val) => overrideLanguage(val?.toLowerCase())}
        />
        <RemovableStringValueInput
          id="country"
          label="Country"
          minLength={2}
          maxLength={2}
          initialValue={localeOverride?.country}
          onValueSet={(val) => overrideCountry(val?.toUpperCase())}
        />
      </Paper>
      <Paper
        className={classes.paper}
        data-qa-selector="metadata-feature-flags"
      >
        <Typography variant="h4">Feature Flags</Typography>
        <Box
          component="form"
          data-qa-selector="feature-flags-form"
          onSubmit={(ev) => {
            ev.preventDefault()
            if (!flagName) return
            loadFeatureFlag(flagName)
          }}
        >
          <Grid container spacing={2}>
            <Grid item sm={6} xs={12}>
              <TextField
                fullWidth
                required
                data-qa-selector="flag-name-input"
                id="flag-name"
                label="Flag Name"
                value={flagName}
                onChange={(ev) => setFlagName(ev.target.value)}
              />
            </Grid>
            <Grid item sm={3} xs={12} className={classes.inputAlignedButton}>
              <Button
                fullWidth
                data-qa-selector="submit"
                type="submit"
                variant="contained"
                disabled={!flagName}
              >
                Check Eligibility
              </Button>
            </Grid>
          </Grid>
          <Grid container spacing={2}>
            <Grid item xs={12}>
              {featureFlags.length && (
                <Typography variant="h5">Check History</Typography>
              )}
              <FeatureFlagCheckTable flags={featureFlags} />
            </Grid>
          </Grid>
        </Box>
      </Paper>
    </>
  )
}

export default Metadata
